import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, CheckCircle2, Loader2, Undo2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BarcodeScanner } from "@/components/colis/BarcodeScanner";
import { ChampLecteur } from "@/components/colis/ChampLecteur";
import { ChariotVisuel } from "@/components/depot/ChariotVisuel";
import { EtagereVisuel } from "@/components/depot/EtagereVisuel";
import { fmtDateHeure } from "@/components/scans/common";
import { OPTIONS_DEPOT } from "@/lib/barcode";
import { api, errMsg } from "@/lib/api";
import { bip } from "@/lib/bip";
import { lireCode, numeroLisible } from "@/lib/colisCode";
import { colisSubnav } from "@/lib/subnavs";
import { useDouchette } from "@/lib/douchette";

const norm = (c) => (c || "").replace(/[\s\x00-\x1f]/g, "").toUpperCase(); // eslint-disable-line no-control-regex
const cleEmp = (id) => `inventaire-emplacement-${id}`;

/**
 * Inventaire du dépôt restant : on scanne le code d'un chariot / étage, puis
 * ses colis. Scanner un autre code de chariot change d'emplacement. Caméra
 * (en continu) et saisie au clavier / douchette en même temps.
 */
export default function InventairePage({ zone = "depot" }) {
  // zone « cage » : même inventaire, sur les étagères de la cage.
  const cage = zone === "cage";
  const P = { params: { zone } };
  const retourUrl = cage ? "/colis/cage" : "/colis/depot";
  const lieu = cage ? "la place de l'étagère" : "le chariot / l'étage";
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [chariots, setChariots] = useState([]);
  const [precedent, setPrecedent] = useState(null); // état du dernier inventaire validé
  const [courant, setCourant] = useState(null); // emplacement en cours (avec chariot)
  const [camera, setCamera] = useState(false); // lecteur intégré par défaut ; caméra sur demande
  const [saisie, setSaisie] = useState("");
  const [retour, setRetour] = useState(null);
  const [valider, setValider] = useState(false);
  const [garder, setGarder] = useState(false);
  const [busy, setBusy] = useState(false);
  const file = useRef(Promise.resolve()); // traitement des scans un par un, dans l'ordre
  const courantRef = useRef(null);
  courantRef.current = courant;
  const champ = useRef(null);

  // Code-barres -> emplacement (chariots actifs).
  const index = useMemo(() => {
    const m = {};
    chariots.filter((c) => c.actif !== false).forEach((ch) => ch.emplacements.forEach((e) => { m[e.code] = { ...e, chariot: ch }; }));
    return m;
  }, [chariots]);
  const indexRef = useRef(index);
  indexRef.current = index;

  const charger = useCallback(async () => {
    try {
      const [i, c, e] = await Promise.all([api.post("/depot/inventaire", null, P), api.get("/depot/chariots", P), api.get("/depot/etat", P)]);
      setInv(i.data); setChariots(c.data.chariots); setPrecedent(e.data);
      // Reprise : emplacement en cours mémorisé sur ce téléphone.
      let id = null;
      try { id = localStorage.getItem(cleEmp(i.data.id)); } catch { /* stockage indisponible */ }
      const emp = c.data.chariots.flatMap((ch) => ch.emplacements.map((x) => ({ ...x, chariot: ch }))).find((x) => x.id === id);
      if (emp) setCourant(emp);
    } catch (err) { toast.error(errMsg(err)); }
  }, [zone]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, [charger]);

  // Les scans des collègues (inventaire à plusieurs) : rafraîchi toutes les 10 s.
  useEffect(() => {
    if (!inv) return undefined;
    const t = setInterval(async () => {
      try { const { data } = await api.get("/depot/inventaire", P); if (data && data.id === inv.id) setInv(data); } catch { /* réseau */ }
    }, 10000);
    return () => clearInterval(t);
  }, [inv?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const choisirEmplacement = (emp) => {
    setCourant(emp);
    try { localStorage.setItem(cleEmp(inv.id), emp.id); } catch { /* stockage indisponible */ }
    bip(true);
    setRetour({ texte: `▶ ${emp.nom}`, ok: true, t: Date.now() });
  };

  const traiter = useCallback(async (brut, source) => {
    const code = norm(brut);
    const emp = indexRef.current[code];
    if (emp) { choisirEmplacement(emp); return true; }
    const lu = lireCode(brut);
    if (!lu.type) {
      bip(false);
      setRetour({ texte: `Code inconnu : ${code.slice(0, 24)}`, ok: false, t: Date.now() });
      return false;
    }
    const cur = courantRef.current;
    if (!cur) {
      bip(false);
      setRetour({ texte: `Scannez d'abord le code de ${lieu}`, ok: false, t: Date.now() });
      return false;
    }
    try {
      const { data } = await api.post(`/depot/inventaire/${inv.id}/scan`, { code: brut, emplacement_id: cur.id, source });
      if (data.doublon) {
        bip(false);
        setRetour({ texte: `Déjà sur ${cur.nom} : ${numeroLisible(lu.numero)}`, ok: false, t: Date.now() });
        return true;
      }
      bip(true);
      setInv((x) => ({ ...x, nb: data.nb, items: [...x.items.filter((i) => i.numero !== data.item.numero), data.item] }));
      const dest = data.item.info?.destinataire?.rs ? ` · ${data.item.info.destinataire.rs}` : "";
      setRetour({ texte: data.deplace_de ? `✓ ${numeroLisible(data.item.numero)} déplacé de ${data.deplace_de}${dest}` : `✓ ${numeroLisible(data.item.numero)} → ${cur.court || `${cur.chariot.lettre}${cur.etage ? ` ${cur.etage}` : ""}`} (${data.nb_emplacement})${dest}`, ok: true, t: Date.now() });
      return true;
    } catch (err) {
      bip(false);
      setRetour({ texte: errMsg(err), ok: false, t: Date.now() });
      return false;
    }
  }, [inv?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const enFile = useCallback((brut, source) => {
    const p = file.current.then(() => traiter(brut, source));
    file.current = p.catch(() => {});
    return p;
  }, [traiter]);

  // Lecteur intégré (Zebra) / douchette : pas besoin de cliquer dans le champ.
  useDouchette((code) => { setSaisie(""); enFile(code, "scan"); }, !!inv?.id);

  const envoyerSaisie = async () => {
    if (!saisie.trim()) return;
    if (await enFile(saisie, "saisie")) setSaisie("");
    champ.current?.focus();
  };

  const annuler = async (it) => {
    try { await api.delete(`/depot/inventaire/${inv.id}/items/${it.numero}`); setInv((x) => ({ ...x, nb: x.nb - 1, items: x.items.filter((i) => i.numero !== it.numero) })); }
    catch (err) { toast.error(errMsg(err)); }
  };

  // Compte par emplacement (inventaire en cours).
  const parEmp = useMemo(() => {
    const m = {};
    (inv?.items || []).forEach((it) => { m[it.emplacement_id] = (m[it.emplacement_id] || 0) + 1; });
    return m;
  }, [inv]);
  const nonScannes = useMemo(() => (precedent?.chariots || []).flatMap((ch) => ch.emplacements.map((e) => ({ ...e, chariot: ch })))
    .filter((e) => !parEmp[e.id] && e.colis.length > 0), [precedent, parEmp]);

  const finir = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/depot/inventaire/${inv.id}/valider`, { garder_non_scannes: garder });
      try { localStorage.removeItem(cleEmp(inv.id)); } catch { /* stockage indisponible */ }
      toast.success(`Inventaire validé : ${data.nb} colis ${cage ? "dans la cage" : "au dépôt"}${data.nb_gardes ? ` (dont ${data.nb_gardes} gardés des emplacements non scannés)` : ""}`);
      navigate(retourUrl);
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  const abandonner = async () => {
    if (!window.confirm("Abandonner cet inventaire ? Les scans seront perdus (tout reste tel qu'au dernier inventaire validé).")) return;
    try { await api.delete(`/depot/inventaire/${inv.id}`); navigate(retourUrl); } catch (err) { toast.error(errMsg(err)); }
  };

  const recents = (inv?.items || []).slice(-12).reverse();

  return (
    <AppShell title={cage ? "Inventaire de la cage" : "Inventaire dépôt restant"} subnav={colisSubnav()}>
      <Link to={retourUrl} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft size={15} /> {cage ? "Colis cage" : "Dépôt restant"}</Link>
      {!inv ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight">{cage ? "Inventaire de la cage" : "Inventaire dépôt restant"}</h1>
              <p className="text-sm text-muted-foreground">
                Commencé le {fmtDateHeure(inv.debut)} par {inv.par?.nom || "—"} · <b className="text-foreground" data-testid="inv-nb">{inv.nb}</b> colis scanné(s)
                {inv.participants?.length > 1 && ` · ${inv.participants.length} personnes`}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" className="text-destructive" onClick={abandonner} data-testid="inv-abandonner"><XCircle size={15} /> Abandonner</Button>
              <Button onClick={() => setValider(true)} disabled={!inv.nb && !nonScannes.length} data-testid="inv-valider"><CheckCircle2 size={15} /> Valider l'inventaire</Button>
            </div>
          </div>

          {chariots.length === 0 && <p className="mb-4 text-sm text-red-600 dark:text-red-400">{cage ? "Aucune étagère : un responsable doit d'abord les créer (« Gérer les étagères »)." : "Aucun chariot : un administrateur doit d'abord les créer (« Gérer les chariots »)."}</p>}

          <div className={`mb-4 rounded-xl border-2 p-3 flex items-center gap-4 ${courant ? "border-emerald-500/60 bg-emerald-500/10" : "border-dashed border-amber-500/60 bg-amber-500/10"}`} data-testid="inv-courant">
            {courant ? (
              <>
                {cage ? <EtagereVisuel lettre={courant.chariot.lettre} lignes={courant.chariot.lignes} colonnes={courant.chariot.colonnes} compact
                  emplacements={courant.chariot.emplacements.map((e) => ({ ...e, nb: parEmp[e.id] || 0 }))} actif={courant.id} /> : <ChariotVisuel type={courant.chariot.type} lettre={courant.chariot.lettre} emplacements={courant.chariot.emplacements.map((e) => ({ ...e, nb: parEmp[e.id] || 0 }))} actif={courant.id} taille={0.55} />}
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Emplacement en cours</p>
                  <p className="text-xl font-bold">{courant.nom}</p>
                  <p className="text-sm text-muted-foreground">{parEmp[courant.id] || 0} colis · scannez les colis, ou {cage ? "une autre place" : "un autre chariot"} pour changer</p>
                </div>
              </>
            ) : <p className="font-semibold">① Scannez d'abord le code-barres {cage ? "de la place de l'étagère" : "du chariot ou de l'étage"}, puis ses colis.</p>}
          </div>

          <section className="grid gap-4 lg:grid-cols-2 mb-6">
            <div className="rounded-xl border border-border bg-card p-4">
              <ChampLecteur ref={champ} value={saisie} onChange={setSaisie} onSubmit={envoyerSaisie} testId="inv-saisie"
                placeholder={cage ? "Place ou colis…" : "Chariot ou colis…"} actif={!valider} />
              {retour && <p className={`mt-3 text-sm font-semibold ${retour.ok ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`} data-testid="inv-retour">{retour.texte}</p>}
              <p className="mt-4 text-xs uppercase tracking-wider text-muted-foreground mb-1">Derniers scans</p>
              {recents.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : (
                <ul className="text-sm divide-y divide-border" data-testid="inv-recents">
                  {recents.map((it) => (
                    <li key={it.numero} className="flex items-center gap-2 py-1.5">
                      <span className="font-mono font-semibold">{numeroLisible(it.numero)}</span>
                      <span className="text-xs text-muted-foreground truncate">
                        {it.emplacement}
                        {it.info?.destinataire?.rs ? <> · <span className="text-foreground font-medium">{it.info.destinataire.rs}</span></> : it.par?.nom ? ` · ${it.par.nom}` : ""}
                      </span>
                      <button onClick={() => annuler(it)} className="ml-auto p-1 text-muted-foreground hover:text-destructive" title="Retirer de l'inventaire"><Undo2 size={14} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              {camera ? (
                <BarcodeScanner continu retour={retour} options={OPTIONS_DEPOT}
                  accepter={(t) => !!indexRef.current[norm(t)] || !!lireCode(t).type}
                  priorite={(t) => (indexRef.current[norm(t)] ? 0 : 1)}
                  aide={courant ? `Colis → ${courant.nom}` : `Visez l'étiquette de ${lieu}`}
                  onDetected={(r) => enFile(r.text, "scan")} onCancel={() => setCamera(false)} />
              ) : (
                <div className="flex flex-col items-start gap-2">
                  <Button variant="outline" onClick={() => setCamera(true)} data-testid="inv-camera"><Camera size={15} /> Activer le scan caméra</Button>
                  <p className="text-xs text-muted-foreground">Inutile avec un terminal Zebra ou une douchette : scannez directement, le champ reste prêt.</p>
                </div>
              )}
            </div>
          </section>

          <div className="flex flex-wrap items-end gap-x-5 gap-y-3" data-testid="inv-chariots">
            {chariots.map((ch) => (cage ? (
              <EtagereVisuel key={ch.id} lettre={ch.lettre} lignes={ch.lignes} colonnes={ch.colonnes} compact actif={courant?.id}
                emplacements={ch.emplacements.map((e) => ({ ...e, nb: parEmp[e.id] || 0 }))} onSelect={(e) => choisirEmplacement({ ...e, chariot: ch })} />
            ) : (
              <div key={ch.id} className="flex flex-col items-center text-foreground">
                <ChariotVisuel type={ch.type} lettre={ch.lettre} emplacements={ch.emplacements.map((e) => ({ ...e, nb: parEmp[e.id] || 0 }))}
                  actif={courant?.id} taille={0.7} onSelect={(e) => choisirEmplacement({ ...e, nom: e.nom, chariot: ch })} />
              </div>)
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">Astuce : un clic sur {cage ? "une place la choisit" : "un chariot le choisit"} aussi comme emplacement en cours (si son étiquette est abîmée).</p>
        </>
      )}

      <Dialog open={valider} onOpenChange={(o) => !o && !busy && setValider(false)}>
        <DialogContent className="sm:max-w-md" data-testid="inv-valider-dialog">
          <DialogHeader>
            <DialogTitle>Valider l'inventaire</DialogTitle>
            <DialogDescription>{inv?.nb || 0} colis scanné(s) : ils remplacent le contenu des {cage ? "places" : "chariots"} scannés.</DialogDescription>
          </DialogHeader>
          <ul className="text-sm space-y-1">
            {chariots.flatMap((ch) => ch.emplacements.map((e) => ({ ...e, ch }))).filter((e) => parEmp[e.id]).map((e) => (
              <li key={e.id} className="flex justify-between"><span>{e.nom}</span><b>{parEmp[e.id]}</b></li>
            ))}
          </ul>
          {nonScannes.length > 0 && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm space-y-2" data-testid="inv-non-scannes">
              <p className="font-semibold">Non scannés dans cet inventaire :</p>
              <ul className="space-y-0.5">{nonScannes.map((e) => <li key={e.id}>{e.nom} — {e.colis.length} colis au dernier inventaire</li>)}</ul>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={garder} onChange={(e) => setGarder(e.target.checked)} data-testid="inv-garder" />
                Garder leur contenu (sinon ils seront vidés)
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setValider(false)} disabled={busy}>Continuer l'inventaire</Button>
            <Button onClick={finir} disabled={busy} data-testid="inv-confirmer">{busy && <Loader2 size={14} className="animate-spin" />} Valider</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
